import { hasOwnProperty, isEnumValue } from "@glideapps/ts-necessities";

// Please prefix the non-free values with v1/v2/etc based on which iteration of Glide's pricing model they belong to.

export enum PlanKind {
    Free = "free",
    // Current "Enterprise" plan sold manually by the sales team.
    Unlimited = "unlimited",
}

// If true, users should be forbidden from intentionally upgrading to, or downgrading away from, the given `PlanKind`.
// Under no circumstances should these be handled by anyone but the sales team.
export function planKindIsUpgradeLocked(x: PlanKind): boolean {
    switch (x) {
        case PlanKind.Unlimited:
            return true;
        default:
            return false;
    }
}

export enum AddonKind {
    // These addons grant individual apps their own plan.
    // They are backward-compatible with previous billing systems.
    V1ProApp = "v1-pro-app",
    V1OrgProPublic = "v1-org-pro-public",
    // This addon is both an app plan addon, as well as a metered addon.
    V1OrgProInternalUserOverage = "v1-org-pro-internal-user-overage",
    // These addons represent "app plans" in the unified pricing model.
    V2BasicPublicApp = "v2-basic-public-app",
    V2BasicPrivateApp = "v2-basic-private-app",
    V2ProPublicApp = "v2-pro-public-app",
    V2ProPrivateApp = "v2-pro-private-app",
    V2ProPrivatePage = "v2-pro-private-page",
    // These represent currently-existing addons in the unified pricing model.
    V2BasicPrivateUser = "v2-basic-private-user",
    V2ProPrivateUser = "v2-pro-private-user",
    V2ProPrivatePageUser = "v2-pro-private-page-user",
    V2Boost1000Reads = "v2-boost-1000-reads",
    V2Boost500Rows = "v2-boost-500-rows",
    V2Boost1000Rows = "v2-boost-1000-rows",
    V2Boost2500Rows = "v2-boost-2500-rows",
    V2Boost5000Rows = "v2-boost-5000-rows",
    // This one is conceptual - we know we want a more robust quota system for handling overages, and we want users to
    // be able to opt out of being charged for overages.
    V3HardCapQuotas = "v3-hard-cap-quotas",
    V3AllowWhiteLabelling = "v3-allow-white-labelling",
    // Other overages don't affect Eminence, they are used only for charging the customer at invoice-time.
    V2BasicPrivateUserOverage = "v2-basic-private-user-overage",
    V2ProPrivateUserOverage = "v2-pro-private-user-overage",
    V2ProPrivatePageUserOverage = "v2-pro-private-page-user-overage",
}

export type BillingPeriod = "monthly" | "annual";

export function isBillingPeriod(x: unknown): x is BillingPeriod {
    return x === "monthly" || x === "annual";
}

// TODO (post-launch): Make the App/Page plans into an enum that is mutually exclusive with the other Addons. They
// should still be stored as addons inside Subscriptions, but their type definitions ought to be separate to avoid
// needing to use this function.
export function addonIsAppPlan(x: AddonKind) {
    switch (x) {
        case AddonKind.V1ProApp:
        case AddonKind.V1OrgProPublic:
        case AddonKind.V1OrgProInternalUserOverage:
        case AddonKind.V2BasicPublicApp:
        case AddonKind.V2BasicPrivateApp:
        case AddonKind.V2ProPublicApp:
        case AddonKind.V2ProPrivateApp:
        case AddonKind.V2ProPrivatePage:
            return true;
        default:
            return false;
    }
}

// If an app plan is being canceled, these addons must be canceled as well.
export function addonCancelsWithAppPlan(x: AddonKind) {
    switch (x) {
        case AddonKind.V2BasicPrivateUser:
        case AddonKind.V2ProPrivateUser:
        case AddonKind.V2ProPrivatePageUser:
        case AddonKind.V3AllowWhiteLabelling:
            return true;
        default:
            return false;
    }
}

// If we're upgrading an app to an app plan, these app addons must be canceled beforehand.
export function addonCancelsWhenSubscribingToAppPlan(x: AddonKind) {
    return addonIsAppPlan(x) || addonCancelsWithAppPlan(x);
}

export function addonRequiresAppID(kind: AddonKind) {
    return addonIsAppPlan(kind) || addonCancelsWithAppPlan(kind);
}

export function addonIsMetered(x: AddonKind) {
    switch (x) {
        case AddonKind.V1OrgProInternalUserOverage:
        case AddonKind.V2BasicPrivateUserOverage:
        case AddonKind.V2ProPrivateUserOverage:
        case AddonKind.V2ProPrivatePageUserOverage:
            return true;
        default:
            return false;
    }
}

export function privateUserAddonKind(appPlanKind: AddonKind): AddonKind | undefined {
    if (!addonIsAppPlan(appPlanKind)) return undefined;
    switch (appPlanKind) {
        case AddonKind.V2BasicPrivateApp:
            return AddonKind.V2BasicPrivateUser;
        case AddonKind.V2ProPrivateApp:
            return AddonKind.V2ProPrivateUser;
        case AddonKind.V2ProPrivatePage:
            return AddonKind.V2ProPrivatePageUser;
        default:
            return undefined;
    }
}

// NOTE REGARDING STRIPE DATA
// Broadly speaking the types that contain Stripe data should be used only in the back-end.  When sending a Subscription
// to the front-end (if necessary at all), cast it from SubscriptionWithStripeData to Subscription instead, so we don't
// leak any Stripe information.

export interface SubscriptionItemStripeData {
    stripeSubscriptionItemID: string;
    stripePriceID: string;
}

export function isSubscriptionItemStripeData(x: unknown): x is SubscriptionItemStripeData {
    return (
        hasOwnProperty(x, "stripeSubscriptionItemID") &&
        typeof x.stripeSubscriptionItemID === "string" &&
        hasOwnProperty(x, "stripePriceID") &&
        typeof x.stripePriceID === "string"
    );
}

export interface SubscriptionAddon {
    // Some addons are applied on a per-app basis, such as all legacy/unified plans, or white-labelling.
    appID?: string;
    kind: AddonKind;
    count: number;
    period?: BillingPeriod;
}

export function isSubscriptionAddon(x: unknown): x is SubscriptionAddon {
    return (
        (!hasOwnProperty(x, "appID") || x.appID === undefined || typeof x.appID === "string") &&
        hasOwnProperty(x, "kind") &&
        isEnumValue(AddonKind, x.kind) &&
        hasOwnProperty(x, "count") &&
        typeof x.count === "number" &&
        (!hasOwnProperty(x, "period") || x.period === undefined || x.period === "monthly" || x.period === "annual")
    );
}

export interface SubscriptionAddonWithStripeData extends SubscriptionAddon, SubscriptionItemStripeData {}

export function isSubscriptionAddonWithStripeData(x: unknown): x is SubscriptionAddonWithStripeData {
    return isSubscriptionAddon(x) && isSubscriptionItemStripeData(x);
}

interface SubscriptionBase {
    subscriptionStatus: string;
    plan: PlanKind;
    period: BillingPeriod;
    stripePromoCode?: string;
}

export function isSubscriptionValid(subscriptionStatus: string): boolean {
    switch (subscriptionStatus) {
        case "incomplete_expired":
        case "canceled":
        case "unpaid":
            return false;
        default:
            return true;
    }
}

export function isSubscriptionComplete(subscriptionStatus: string): boolean {
    if (!isSubscriptionValid(subscriptionStatus)) return false;
    switch (subscriptionStatus) {
        case "incomplete":
        case "past_due":
            return false;
        default:
            return true;
    }
}

function isSubscriptionBase(x: unknown): x is SubscriptionBase {
    return (
        hasOwnProperty(x, "subscriptionStatus") &&
        typeof x.subscriptionStatus === "string" &&
        hasOwnProperty(x, "plan") &&
        isEnumValue(PlanKind, x.plan) &&
        hasOwnProperty(x, "period") &&
        isBillingPeriod(x.period) &&
        (!hasOwnProperty(x, "stripePromoCode") ||
            x.stripePromoCode === undefined ||
            typeof x.stripePromoCode === "string")
    );
}

export interface SubscriptionPlan extends SubscriptionBase {
    addons: SubscriptionAddon[];
}

export function isSubscriptionPlan(x: unknown): x is SubscriptionPlan {
    return (
        isSubscriptionBase(x) &&
        hasOwnProperty(x, "addons") &&
        Array.isArray(x.addons) &&
        x.addons.every(isSubscriptionAddon)
    );
}

export interface SubscriptionPlanWithStripeData extends SubscriptionBase {
    stripeSubscriptionItemID: string;
    stripePriceID: string;
    addons: SubscriptionAddonWithStripeData[];
}

export function isSubscriptionPlanWithStripeData(x: unknown): x is SubscriptionPlanWithStripeData {
    return (
        isSubscriptionBase(x) &&
        hasOwnProperty(x, "stripeSubscriptionItemID") &&
        typeof x.stripeSubscriptionItemID === "string" &&
        hasOwnProperty(x, "stripePriceID") &&
        typeof x.stripePriceID === "string" &&
        hasOwnProperty(x, "addons") &&
        Array.isArray(x.addons) &&
        x.addons.every(isSubscriptionAddonWithStripeData)
    );
}

export interface Subscription extends SubscriptionPlan {
    ownerID: string;
}

export function isSubscription(x: unknown): x is Subscription {
    return isSubscriptionPlan(x) && hasOwnProperty(x, "ownerID") && typeof x.ownerID === "string";
}

export interface SubscriptionStripeData {
    stripeSubscriptionID: string;
    stripeCustomerID: string;
}

export interface SubscriptionWithStripeData extends SubscriptionStripeData, SubscriptionPlanWithStripeData {
    ownerID: string;
}

export function isSubscriptionWithStripeData(x: unknown): x is SubscriptionWithStripeData {
    return (
        hasOwnProperty(x, "stripeSubscriptionID") &&
        typeof x.stripeSubscriptionID === "string" &&
        hasOwnProperty(x, "stripeCustomerID") &&
        typeof x.stripeCustomerID === "string" &&
        hasOwnProperty(x, "ownerID") &&
        typeof x.ownerID === "string" &&
        isSubscriptionPlanWithStripeData(x)
    );
}

export function defaultSubscriptionForOwner(ownerID: string, period: BillingPeriod = "monthly"): Subscription {
    return {
        ownerID,
        subscriptionStatus: "",
        plan: PlanKind.Free,
        period,
        addons: [],
    };
}

function removeAddonStripeData(addon: SubscriptionAddonWithStripeData): SubscriptionAddon {
    return {
        appID: addon.appID ?? undefined,
        kind: addon.kind,
        count: addon.count,
        period: addon.period ?? undefined,
    };
}

export function removeSubscriptionStripeData(sub: SubscriptionWithStripeData): Subscription {
    return {
        ownerID: sub.ownerID,
        subscriptionStatus: sub.subscriptionStatus,
        plan: sub.plan,
        period: sub.period,
        addons: sub.addons.map(removeAddonStripeData),
        stripePromoCode: sub.stripePromoCode ?? undefined,
    };
}

// This is for bookkeeping, so that we can change prices in real-time without needing to deploy changes to our code.
// Price data is only necessary for one kind of operation: making changes to a Stripe subscription.  It won't be used
// elsewhere.

// TODO: Consider creating the following types:
// interface PriceBase { ... }
// interface PlanPrice extends PriceBase { ... }
// interface AddonPrice extends PriceBase { ... }
// type Price = PlanPrice | AddonPrice;
// Which would more rigidly enforce the requirement for one of planKind or addonKind.

export interface Price {
    stripePriceID: string;
    stripeProductID: string;
    planKind: PlanKind | undefined;
    addonKind: AddonKind | undefined;
    currency: string;
    unitAmount: number;
    period: BillingPeriod;
    // This number is a timestamp from Stripe, which is the number of seconds since the start of Time (1 Jan 1970)
    created: number;
}

export function isPrice(x: unknown): x is Price {
    return (
        hasOwnProperty(x, "stripePriceID") &&
        typeof x.stripePriceID === "string" &&
        hasOwnProperty(x, "stripeProductID") &&
        typeof x.stripeProductID === "string" &&
        // Note closely: the plan and addon kind here are mutually exclusive.
        // Exactly one is required for a valid price.
        hasOwnProperty(x, "planKind") &&
        hasOwnProperty(x, "addonKind") &&
        (isEnumValue(PlanKind, x.planKind) || isEnumValue(AddonKind, x.addonKind)) &&
        (x.planKind === undefined || x.addonKind === undefined) &&
        hasOwnProperty(x, "currency") &&
        typeof x.currency === "string" &&
        hasOwnProperty(x, "unitAmount") &&
        typeof x.unitAmount === "number" &&
        hasOwnProperty(x, "period") &&
        (x.period === "monthly" || x.period === "annual") &&
        hasOwnProperty(x, "created") &&
        typeof x.created === "number"
    );
}
